import { Step } from "react-joyride";
import { VisibilityStates } from "../../types/VisibilityStates";

export const tourStepsCollectionSetup = (
  visibilityStates: VisibilityStates,
  category: string,
  type: string,
  dotCountType?: string,
): Step[] => {
  const steps: Step[] = [
    {
      target: ".collection-setup",
      content:
        "Step 2: choose how items are made. You can generate a random set or add custom items, depending on the category.",
      disableBeacon: true,
    },
  ];

  if (category === "Picture Quiz") {
    steps.push({
      target: ".picture-quiz-upload",
      content:
        "Upload images for your quiz. Each picture needs a question and a required answer before you can save.",
    });
  } else if (category === "Picture Words") {
    steps.push({
      target: ".picture-words-upload",
      content:
        "Upload images and enter the matching word for each picture. Every image needs a word before you can save.",
    });
  } else if (category === "Domino Comparison") {
    steps.push(
      {
        target: "#minDominoDots",
        content: "Lowest number of dots allowed on a domino side.",
        ...(visibilityStates.isMinDotsVisible ? { isOpen: true } : {}),
      },
      {
        target: "#maxDominoDots",
        content: "Highest number of dots allowed on a domino side.",
        ...(visibilityStates.isMaxDotsVisible ? { isOpen: true } : {}),
      },
      {
        target: "#dominoDotColor",
        content: "Color of the dots on each generated domino.",
        ...(visibilityStates.isDotColorVisible ? { isOpen: true } : {}),
      },
    );
  } else if (category === "Number Sense") {
    steps.push({
      target: "#dotCountType",
      content:
        "Random gives each card a different number of dots between your min and max. Fixed uses the same count on every card.",
      ...(visibilityStates.isDotCountTypeVisible ? { isOpen: true } : {}),
    });

    if (dotCountType === "fixed") {
      steps.push({
        target: "#itemCount",
        content: "How many dots appear on each generated card.",
        ...(visibilityStates.isItemCountVisible ? { isOpen: true } : {}),
      });
    } else if (dotCountType === "random") {
      steps.push(
        {
          target: "#minDots",
          content: "Smallest number of dots a card can have.",
          ...(visibilityStates.isMinDotsVisible ? { isOpen: true } : {}),
        },
        {
          target: "#maxDots",
          content: "Largest number of dots a card can have.",
          ...(visibilityStates.isMaxDotsVisible ? { isOpen: true } : {}),
        },
      );
    }

    steps.push(
      {
        target: "#collectionItemCount",
        content:
          "How many cards to generate. This is not the number of dots on each card.",
        ...(visibilityStates.isCollectionItemCountVisible
          ? { isOpen: true }
          : {}),
      },
      {
        target: "#dot-color",
        content:
          "Pick one color for every card, or several so the generator mixes them.",
        ...(visibilityStates.isDotColorVisible ? { isOpen: true } : {}),
      },
      {
        target: "#dot-shape",
        content:
          "Pick one shape for every card, or several so the generator mixes them.",
        ...(visibilityStates.isDotShapeVisible ? { isOpen: true } : {}),
      },
      {
        target: "#dot-mix-mode",
        content:
          "Never keeps each card one color and shape. Sometimes or Always can mix them on one card, then ask how many triangles or how many are red.",
      },
    );
  } else {
    if (
      !["numbersOneToHundred", "alphabet", "fullPeriodicTable"].includes(type)
    ) {
      steps.push({
        target: "#itemCount",
        content:
          "How many items to generate. Type the number you want. Scrolling over this box will not change it.",
        ...(visibilityStates.isItemCountVisible ? { isOpen: true } : {}),
      });
    }
  }

  steps.push({
    target: ".generate-random-sequence-button",
    content:
      "Generate fills the preview with a random set based on the options above. You can still edit items on the next step.",
    ...(visibilityStates.isGenerateRandomSequenceButtonVisible
      ? { isOpen: true }
      : {}),
  });

  steps.push({
    target: ".create-custom-items-button",
    content:
      "Create Custom Items lets you type your own items or upload images instead of using a generated list.",
  });

  if (visibilityStates.isGeneratedSequencePreviewVisible) {
    steps.push({
      target: ".generated-sequence-preview",
      content:
        "This is a preview of what will be in the collection. Check it before you continue.",
    });
  }

  steps.push(
    {
      target: ".clear-button",
      content: "Clear removes the generated preview so you can start over.",
      ...(visibilityStates.isClearButtonVisible ? { isOpen: true } : {}),
    },
    {
      target: ".next-button",
      content:
        "Next takes you to the last step, where you can add more items and save the collection.",
      ...(visibilityStates.isNextButtonVisible ? { isOpen: true } : {}),
    },
  );

  return steps.filter((step) => {
    const target = step.target as string;
    const visibilityMap: Record<string, boolean> = {
      "#dotCountType": visibilityStates.isDotCountTypeVisible,
      "#minDots": visibilityStates.isMinDotsVisible,
      "#maxDots": visibilityStates.isMaxDotsVisible,
      "#itemCount": visibilityStates.isItemCountVisible,
      "#collectionItemCount": visibilityStates.isCollectionItemCountVisible,
      "#dot-color": visibilityStates.isDotColorVisible,
      "#dot-shape": visibilityStates.isDotShapeVisible,
      ".generate-random-sequence-button":
        visibilityStates.isGenerateRandomSequenceButtonVisible,
      ".generated-sequence-preview":
        visibilityStates.isGeneratedSequencePreviewVisible,
      ".clear-button": visibilityStates.isClearButtonVisible,
      ".next-button": visibilityStates.isNextButtonVisible,
    };

    return target in visibilityMap ? visibilityMap[target] : true;
  });
};
